/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useState, ReactNode } from 'react';
import { Input, message, DatePicker } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import INVOICES_FOR_LOG_AR_QUERY, {
  InvoicesForLogARData,
  InvoicesForLogARVars,
} from 'shared/graphql/query/invoicesForLogAR';
import { Button, TableSearch, Text } from 'shared/components';
import s3 from 'shared/config/s3';
import { formatDate } from 'shared/utils/dateTime';
import formatCurrency from 'shared/utils/formatCurrency';
import { SearchOutlined, FileTextOutlined } from '@ant-design/icons';
import { ToolsContainer } from './Styles';
import SelectReceiptNumberField from './SelectReceiptNumberField';

const { RangePicker } = DatePicker;

type SuccessTableProps = {
  customerId: string;
};

type SuccessDataTable = {
  key: number;
  tools: ReactNode;
  column1: string;
  column2: string;
  column3: ReactNode;
  column4: ReactNode;
  column5: ReactNode;
  column6: ReactNode;
  column7: ReactNode;
  column8: string;
};

const SuccessTable: React.FC<SuccessTableProps> = (props) => {
  const location = useLocation();
  const { customerId } = props;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [serviceDate, setServiceDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [receiptDate, setReceiptDate] = useState<string>('');
  const [receiptId, setReceiptId] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderType, setOrderType] = useState<'1' | '-1' | ''>('1');

  const { loading, error, data } = useQuery<InvoicesForLogARData, InvoicesForLogARVars>(
    INVOICES_FOR_LOG_AR_QUERY,
    {
      variables: {
        payer: customerId,
        service_date: serviceDate,
        invoice_number: invoiceNumber,
        due_date: dueDate,
        receipt_date: receiptDate,
        receipt: receiptId,
        followSuccess: true,
        showData: pageSize,
        page: currentPage,
        order_by: orderBy,
        order_type: orderType,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const columns = [
    {
      title: 'เครื่องมือ',
      dataIndex: 'tools',
      width: 80,
    },
    {
      title: 'วันที่เอกสาร',
      dataIndex: 'column1',
      width: 210,
      search: (
        <RangePicker
          onChange={(values) => {
            if (values) {
              const formatRangeDate = `${values[0]?.format('YYYY-MM-DD')}:${values[1]?.format(
                'YYYY-MM-DD',
              )}`;
              return setServiceDate(formatRangeDate);
            }
            setServiceDate('');
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'รหัสใบแจ้งหนี้',
      dataIndex: 'column2',
      width: 150,
      search: (
        <Input
          allowClear
          value={invoiceNumber}
          placeholder="ค้นหา"
          onChange={(event) => setInvoiceNumber(event.target.value)}
        />
      ),
    },
    {
      title: 'มูลค่าใบแจ้งหนี้',
      dataIndex: 'column3',
      width: 150,
      align: 'right',
    },
    {
      title: 'วันครบกำหนดชำระ',
      dataIndex: 'column4',
      width: 210,
      search: (
        <RangePicker
          onChange={(values) => {
            if (values) {
              const formatRangeDate = `${values[0]?.format('YYYY-MM-DD')}:${values[1]?.format(
                'YYYY-MM-DD',
              )}`;
              return setDueDate(formatRangeDate);
            }
            setDueDate('');
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'เกินกำหนด',
      dataIndex: 'column5',
      width: 150,
    },
    {
      title: 'อ้างอิงใบวางบิล',
      dataIndex: 'column6',
      width: 130,
    },
    {
      title: 'วันรับชำระ',
      dataIndex: 'column7',
      width: 210,
      search: (
        <RangePicker
          onChange={(values) => {
            if (values) {
              const formatRangeDate = `${values[0]?.format('YYYY-MM-DD')}:${values[1]?.format(
                'YYYY-MM-DD',
              )}`;
              return setReceiptDate(formatRangeDate);
            }
            setReceiptDate('');
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'รหัสใบเสร็จรับเงิน',
      dataIndex: 'column8',
      width: 150,
      search: (
        <SelectReceiptNumberField onChange={(value) => setReceiptId(value)} payerId={customerId} />
      ),
    },
  ];

  let successDataTable: SuccessDataTable[] = [];
  let totalDocument = 0;

  if (data?.invoicesForLogAR) {
    const { invoices } = data.invoicesForLogAR;
    successDataTable = invoices.map(
      (item, index): SuccessDataTable => ({
        key: index,
        tools: (
          <ToolsContainer>
            <Link to={`${location.pathname}/invoice/${item._id}`}>
              <Button color="grey--text-only" icon={<SearchOutlined />} size="small" />
            </Link>
            <Button
              color="grey--text-only"
              icon={<FileTextOutlined />}
              size="small"
              target="_blank"
              disabled={!item?.receipt?.document}
              href={`${s3}/${item?.receipt?.document}`}
            />
          </ToolsContainer>
        ),
        column1: item?.service_date || '-',
        column2: item.invoice_number || '-',
        column3: (
          <Text strong customType="primary">
            {formatCurrency(+item.grand_total_price, '0')}
          </Text>
        ),
        column4: item.due_date || '-',
        column5: item.overDueDays ? `${item.overDueDays} วัน` : '-',
        column6: item.billing_note?.bill_number || '-',
        column7: item.receipt?.receipt_date ? formatDate(item.receipt.receipt_date) : '-',
        column8: item.receipt?.receipt_number || '-',
      }),
    );

    totalDocument = data.invoicesForLogAR.totalDocument;
  }

  if (error) {
    message.error(error.message);
  }

  return (
    <>
      <TableSearch
        columns={columns}
        dataSource={successDataTable}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalDocument,
          showSizeChanger: false,
          onChange: (value) => setCurrentPage(value),
        }}
        onSort={(sort) => {
          setOrderBy(sort.orderBy);
          setOrderType(sort.orderType);
        }}
      />
    </>
  );
};

export default SuccessTable;
